import React, { useState } from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import Logo from "../images/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faSearch } from "@fortawesome/free-solid-svg-icons";
import Search from "./search";
import Menu from "./menu";
import MenuSideNav from "./menu-sidenav";
import Image from "./image";

const WidthWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-flow: row wrap;
  height: auto;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const InnerWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1024px) {
    justify-content: flex-start;
    padding: 15px;
  }
`;

const HeaderWrapper = styled.nav`
  position: sticky;
  top: 0;
  left: 0;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  height: auto;
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  box-shadow: 0px 8px 20px rgba(0, 0, 0, 0.2);
  z-index: 999;
`;
const LeftWrapper = styled.div`
  display: flex;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    align-items: center;
  }
`;
const LogoImage = styled(Image)`
  object-fit: contain;
  max-width: 219px;
  margin-left: 60px;
  filter: ${({ theme }) =>
    theme.isDarkMode ? "grayscale(1) brightness(0) invert(1)" : "unset"};

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin-left: 0;
    height: 45px;
    max-width: 128px;
  }
`;

const StaticLogo = styled.img`
  height: 50px;
  object-fit: contain;
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 75px;
  font-size: 1.3rem;
  padding: 1rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    right: 65px;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Burger = styled(FontAwesomeIcon)`
  margin-left: 15px;
  padding: 10px;
  cursor: pointer;
  z-index: 99;
  font-size: 2.2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin: 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  transition: all 0.4s ease-in-out;
  opacity: ${({ $burgerOpen }) => ($burgerOpen ? "1" : "0")};
  visibility: ${({ $burgerOpen }) => ($burgerOpen ? "visible" : "hidden")};
`;
const LogoLink = styled(Link)`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: flex;
    justify-content: center;
    margin-right: 48px;
    padding: 15px;
    width: 100%;
  }
`;

const Header = ({ staticQueryData, staticSearchData, children }) => {
  const [burgerOpen, setBurgerOpen] = useState(undefined);
  const [searchOpen, setSearchOpen] = useState(false);
  const { wp, headerNav } =
    staticQueryData ||
    useStaticQuery(graphql`
      fragment HeaderNav on WpMenuItem {
        order
        id
        url
        cssClasses
        label
        description
        title
      }
      query HeaderQuery {
        wp {
          crbThemeOptions {
            crbLogoHeader {
              media {
                altText
                sourceUrl
                localFile {
                  childImageSharp {
                    gatsbyImageData(
                      height: 50
                      layout: FIXED
                      placeholder: NONE
                      transformOptions: { fit: CONTAIN }
                    )
                  }
                }
              }
            }
            crbSearchbarEnabled
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbMenuExtraText
          }
          wcSettings {
            cartUrl
          }
        }
        headerNav: wpMenu(locations: { eq: HEADER_NAV }) {
          menuItems {
            nodes {
              childItems {
                nodes {
                  childItems {
                    nodes {
                      ...HeaderNav
                    }
                  }
                  ...HeaderNav
                }
              }
              parentId
              ...HeaderNav
            }
          }
        }
      }
    `);
  return (
    <>
      <HeaderWrapper burgerOpen={burgerOpen}>
        {children}
        <WidthWrapper>
          <InnerWrapper
            style={{
              paddingTop: `${wp?.crbThemeOptions?.crbPaddingTop || "0"}px`,
              paddingRight: `${wp?.crbThemeOptions?.crbPaddingRight || "0"}px`,
              paddingBottom: `${
                wp?.crbThemeOptions?.crbPaddingBottom || "0"
              }px`,
              paddingLeft: `${wp?.crbThemeOptions?.crbPaddingLeft || "0"}px`,
            }}
          >
            <LeftWrapper>
              <Burger
                icon={faBars}
                onClick={() => setBurgerOpen(!burgerOpen)}
              />
              <LogoLink to="/">
                {wp?.crbThemeOptions?.crbLogoHeader?.media ? (
                  <LogoImage
                    alt="Logotype"
                    image={wp?.crbThemeOptions?.crbLogoHeader?.media}
                    loading="eager"
                    objectFit="contain"
                  />
                ) : (
                  <StaticLogo alt="Logotype" src={Logo} />
                )}
              </LogoLink>
            </LeftWrapper>
            {wp?.crbThemeOptions?.crbSearchbarEnabled && (
              <Search
                searchOpen={searchOpen}
                onClose={() => setSearchOpen(false)}
                staticSearchData={staticSearchData}
              />
            )}
            <Menu cartUrl={wp.wcSettings?.cartUrl} headerNav={headerNav} />

            {wp?.crbThemeOptions?.crbSearchbarEnabled && (
              <SearchIcon onClick={() => setSearchOpen(true)}>
                <FontAwesomeIcon icon={faSearch} />
              </SearchIcon>
            )}
          </InnerWrapper>
        </WidthWrapper>
      </HeaderWrapper>
      <MenuSideNav
        setBurgerOpen={setBurgerOpen}
        burgerOpen={burgerOpen}
        headerNav={headerNav}
        wp={wp}
      />
      <Backdrop
        $burgerOpen={burgerOpen}
        onClick={() => setBurgerOpen(undefined)}
      />
    </>
  );
};

Header.propTypes = {
  staticQueryData: PropTypes.object,
  children: PropTypes.node,
  dropdownOpen: PropTypes.bool,
  staticSearchData: PropTypes.object,
};

export default Header;
